import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import {
  getPatientSessions,
  downloadPatientSessionsCsv,
} from "../../services/GameService";
import { formatNum } from "../Patient/StatsDisplay";
import "./PatientStats.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

// Per-patient session report (#49): summary, score trend, session table, and a
// CSV export.
const PatientStats = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    getPatientSessions(id)
      .then(setData)
      .catch(() => setError("Could not load the report."))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="patient-stats">
        <p>Loading…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="patient-stats">
        <p className="pstat-error">{error}</p>
      </div>
    );
  }

  const { patient, summary, sessions } = data;
  const cards = [
    { number: summary.sessions_played, label: "Sessions" },
    { number: summary.completed, label: "Completed" },
    { number: formatNum(summary.best_score), label: "Best score" },
    { number: formatNum(summary.avg_score), label: "Average score" },
  ];

  return (
    <div className="patient-stats">
      <Link to={`/admin/patients/${id}`} className="pstat-back">
        ← Back to patient
      </Link>
      <div className="pstat-header">
        <h1>{patient.username} — report</h1>
        <button
          className="pstat-export"
          onClick={() => downloadPatientSessionsCsv(id)}
        >
          Download CSV
        </button>
      </div>

      <div className="pstat-cards">
        {cards.map((c) => (
          <div className="pstat-card" key={c.label}>
            <span className="pstat-number">{c.number}</span>
            <span className="pstat-label">{c.label}</span>
          </div>
        ))}
      </div>

      {sessions.length > 0 && (
        <div className="pstat-chart">
          <Line
            data={{
              labels: sessions.map((s) =>
                new Date(s.started_at).toLocaleDateString()
              ),
              datasets: [
                {
                  label: "Score",
                  data: sessions.map((s) => s.score),
                  borderColor: "#4a7c59",
                  backgroundColor: "#4a7c59",
                },
              ],
            }}
          />
        </div>
      )}

      {sessions.length === 0 ? (
        <p className="pstat-empty">No sessions recorded yet.</p>
      ) : (
        <table className="pstat-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Game</th>
              <th>Score</th>
              <th>Completed</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((s, i) => (
              <tr key={i}>
                <td>{new Date(s.started_at).toLocaleString()}</td>
                <td>{s.game}</td>
                <td>{formatNum(s.score)}</td>
                <td>{s.completed ? "Yes" : "No"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default PatientStats;
